import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Archive, Loader2, CheckCircle2, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../guias/guiasUtils';

const mb = (b) => `${Math.max(1, Math.round((b || 0) / 1048576))} MB`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const abrirDownload = (url) => {
  const a = document.createElement('a'); a.href = url; a.rel = 'noopener'; a.style.display = 'none';
  document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 2000);
};

export const LoteDialog = ({ open, onOpenChange, api, competencia, params, titulo }) => {
  const [plano, setPlano] = useState(null);
  const [erro, setErro] = useState(null);
  const [estado, setEstado] = useState({});   // numero -> 'gerando' | 'ok' | 'erro'
  const [progresso, setProgresso] = useState({}); // numero -> { feitos, total, paciente }
  const [todas, setTodas] = useState(false);
  const busy = todas || Object.values(estado).includes('gerando');

  useEffect(() => {
    if (!open) return;
    setPlano(null); setErro(null); setEstado({}); setProgresso({});
    api.get(`/fechamento/lote/${competencia}/partes`, { params })
      .then((r) => setPlano(r.data))
      .catch((e) => setErro(errMsg(e, 'Erro ao planejar o lote')));
  }, [open, api, competencia, params]);

  const baixar = async (parte) => {
    setEstado((s) => ({ ...s, [parte.numero]: 'gerando' })); setProgresso((s) => ({ ...s, [parte.numero]: { feitos: 0, total: parte.pacientes } }));
    try {
      const { data: job } = await api.post(`/fechamento/lote/${competencia}/jobs`, { ...params, parte: parte.numero });
      let st = job;
      while (st.status === 'na_fila' || st.status === 'em_andamento') {
        await sleep(2000);
        st = (await api.get(`/fechamento/lote/jobs/${job.id}`)).data;
        setProgresso((s) => ({ ...s, [parte.numero]: { feitos: st.feitos || 0, total: st.total || parte.pacientes, paciente: st.paciente_atual, retomado: (st.tentativas || 1) > 1 } }));
      }
      if (st.status !== 'pronto') throw new Error(st.erro || 'Falha ao gerar o lote');
      abrirDownload(`${api.defaults.baseURL}/fechamento/lote/jobs/${job.id}/download?t=${encodeURIComponent(job.token)}`);
      setEstado((s) => ({ ...s, [parte.numero]: 'ok' }));
      toast.success(`${st.arquivo}: ${st.pdfs} PDFs de ${st.total} pacientes (${mb(st.tamanho)})${st.problemas ? ` · ${st.problemas} com documentos ilegíveis (ver PROBLEMAS.txt)` : ''}`);
      return true;
    } catch (e) {
      setEstado((s) => ({ ...s, [parte.numero]: 'erro' }));
      toast.error(e.response ? errMsg(e, `Erro ao gerar a parte ${parte.numero}`) : (e.message || `Erro ao gerar a parte ${parte.numero}`));
      return false;
    }
  };

  const baixarTodas = async () => {
    setTodas(true);
    for (const p of plano.partes) { if (estado[p.numero] !== 'ok' && !(await baixar(p))) break; }
    setTodas(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden flex flex-col" data-testid="lote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5" />{titulo}</DialogTitle>
          <DialogDescription>Uma pasta por paciente com o PDF consolidado timbrado. Lotes acima de 200 MB são divididos em partes; cada parte é gerada no servidor e o download inicia automaticamente ao concluir.</DialogDescription>
        </DialogHeader>
        {erro && <p className="text-sm text-rose-700 flex items-center gap-2" data-testid="lote-erro"><AlertTriangle className="h-4 w-4" />{erro}</p>}
        {!plano && !erro && <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="lote-planejando"><Loader2 className="h-4 w-4 animate-spin" />Calculando partes do lote...</p>}
        {plano && (
          <div className="space-y-3 min-w-0 flex-1 flex flex-col overflow-hidden">
            <div className="rounded-lg border bg-muted/30 p-3 text-sm" data-testid="lote-resumo">
              <strong data-testid="lote-total-pacientes">{plano.total_pacientes}</strong> pacientes · {mb(plano.total_bytes)} de documentos originais · <strong data-testid="lote-total-partes">{plano.total_partes}</strong> {plano.total_partes === 1 ? 'arquivo' : 'partes'}
            </div>
            <ul className="space-y-2 min-h-0 flex-1 max-h-[50vh] overflow-y-auto overscroll-contain pr-1" data-testid="lote-partes">
              {plano.partes.map((p) => (
                <li key={p.numero} className="flex items-center justify-between gap-3 rounded border p-2 text-sm min-w-0" data-testid={`lote-parte-${p.numero}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-xs sm:text-sm break-all leading-snug">{p.arquivo}</p>
                    <p className="text-xs text-muted-foreground truncate" title={p.nomes.join(', ')}>{p.pacientes} pacientes · ~{mb(p.bytes)} · {p.nomes[0]}{p.pacientes > 1 ? ` … ${p.nomes[p.nomes.length - 1]}` : ''}</p>
                    {estado[p.numero] === 'gerando' && progresso[p.numero] && (
                      <div className="mt-1" data-testid={`lote-progresso-${p.numero}`}>
                        <div className="h-1.5 w-full rounded bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${Math.round(100 * (progresso[p.numero].feitos || 0) / Math.max(1, progresso[p.numero].total || 1))}%` }} /></div>
                        <p className="text-[11px] text-muted-foreground truncate">Gerando PDFs: {progresso[p.numero].feitos}/{progresso[p.numero].total}{progresso[p.numero].paciente ? ` · ${progresso[p.numero].paciente}` : ''}{progresso[p.numero].retomado ? ' · retomado automaticamente' : ''}</p>
                      </div>
                    )}
                  </div>
                  <Button size="sm" className="shrink-0" variant={estado[p.numero] === 'ok' ? 'outline' : 'secondary'} disabled={busy} onClick={() => baixar(p)} data-testid={`lote-baixar-parte-${p.numero}`}>
                    {estado[p.numero] === 'gerando' ? <Loader2 className="h-4 w-4 animate-spin" /> : estado[p.numero] === 'ok' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Download className="h-4 w-4" />}
                    <span className="ml-1">{estado[p.numero] === 'ok' ? 'Baixado' : estado[p.numero] === 'erro' ? 'Tentar de novo' : 'Baixar'}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="lote-fechar">Fechar</Button>
          {plano && <Button onClick={baixarTodas} disabled={busy} data-testid="lote-baixar-todas">{todas ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}{plano.total_partes === 1 ? 'Baixar ZIP' : `Baixar todas (${plano.total_partes} partes)`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
